import {
  FactorySection, HeroMetric, StatusChip, ConveyorDivider,
} from "@/components/FactoryUI";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Area, AreaChart, ResponsiveContainer, Tooltip } from "recharts";
import AnalyzeModal from "@/components/AnalyzeModal";
import ChannelVideosModal from "@/components/ChannelVideosModal";
import IdeaBankModal from "@/components/IdeaBankModal";
import { invalidateActiveChannelsNav } from "@/hooks/useActiveChannelsNav";
import {
  addActiveChannel,
  deleteActiveChannel,
  listActiveChannels,
  refreshActiveChannel,
  getYouTubeLinkStatus,
} from "@/lib/api";

function fmtHandle(raw) {
  // Same sanitizer as the detail page — hide any "@handle" that
  // doesn't match YouTube's allowed character set, because that
  // means the value is actually the channel NAME stored in the
  // wrong field on an older channel doc.
  if (!raw) return "";
  const clean = String(raw).replace(/^@/, "").trim();
  if (!clean) return "";
  if (!/^[A-Za-z0-9_.\-]{3,}$/.test(clean)) return "";
  return clean;
}

function fmtNum(n) {
  if (n === null || n === undefined) return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return String(num);
}

function fmtCurrency(n) {
  if (n === null || n === undefined) return "—";
  const num = Number(n);
  if (Number.isNaN(num) || num === 0) return "—";
  if (num >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(1)}K`;
  return `$${num}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function MiniSparkline({ series, dataKey, color, testid }) {
  // Renders a tiny 40-50px tall sparkline. We DEDUPE-by-date upstream
  // so points are evenly spaced. With <2 points we show a placeholder
  // — Recharts renders ugly empty paths otherwise.
  const data = (series || []).filter((s) => s[dataKey] !== null && s[dataKey] !== undefined);
  if (data.length < 2) {
    return (
      <div
        data-testid={`${testid}-empty`}
        className="h-12 flex items-center text-zinc-500 font-mono text-[9px]"
      >
        chart fills in after daily snapshots
      </div>
    );
  }
  return (
    <div data-testid={testid} className="h-12 -mx-1">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
          <defs>
            <linearGradient id={`grad-${testid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.5} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Tooltip
            cursor={false}
            contentStyle={{
              background: "#0a0a0a",
              border: "1px solid #27272a",
              fontFamily: "monospace",
              fontSize: 10,
              padding: "4px 8px",
            }}
            labelStyle={{ color: "#a1a1aa" }}
            formatter={(v) => [fmtNum(v), dataKey]}
          />
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#grad-${testid})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function ChannelCard({ ch, onRefresh, onDelete, onAnalyze, onNextVideo, onViewVideos, refreshing, ytLinked }) {
  const r = ch.rollup || {};
  const subs = ch.subscribers_hidden
    ? "Hidden"
    : fmtNum(ch.subscribers);
  const subsDelta = r.subs_delta_7d;
  const subsDeltaLabel =
    subsDelta === null || subsDelta === undefined
      ? "—"
      : `${subsDelta >= 0 ? "+" : ""}${fmtNum(subsDelta)}`;
  const series = r.snapshot_series || [];
  return (
    <div
      data-testid={`active-channel-card-${ch.channel_id}`}
      className="border border-zinc-800 hover:border-yellow-400/60 bg-zinc-950 rounded-sm p-5 transition-colors group flex flex-col"
    >
      <Link
        to={`/active-channels/${ch.channel_id}`}
        className="flex items-start gap-4 mb-3"
      >
        {ch.thumbnail ? (
          <div className="relative shrink-0">
            <img
              src={ch.thumbnail}
              alt=""
              className="h-14 w-14 rounded-full border border-zinc-800"
              referrerPolicy="no-referrer"
            />
            <YouTubeStatusDot linked={ytLinked} channelId={ch.channel_id} />
          </div>
        ) : (
          <div className="relative shrink-0">
            <div className="h-14 w-14 rounded-full bg-zinc-900" />
            <YouTubeStatusDot linked={ytLinked} channelId={ch.channel_id} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg font-bold text-white truncate group-hover:text-yellow-300 transition-colors">
            {ch.name}
          </div>
          {fmtHandle(ch.handle) && (
            <div className="text-xs text-zinc-300 truncate font-mono">
              @{fmtHandle(ch.handle)}
            </div>
          )}
          {r.niche_label && (
            <div
              data-testid={`active-channel-niche-${ch.channel_id}`}
              className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 rounded-sm bg-yellow-400/15 border border-yellow-400/40 font-mono text-[10px] uppercase tracking-wider text-yellow-300"
            >
              <span className="text-yellow-500">◆</span>
              {r.niche_label}
            </div>
          )}
        </div>
      </Link>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <Stat label="subs" value={subs} sub={subsDeltaLabel + " · 7d"} />
        <Stat label="views" value={fmtNum(ch.total_views)} />
        <Stat label="videos" value={fmtNum(ch.video_count)} />
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 mb-3">
        <div className="border border-zinc-900 rounded-sm p-2 bg-black/40">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-300 mb-1">
            subs · last 30 days
          </div>
          <MiniSparkline
            series={series}
            dataKey="subs"
            color="#facc15"
            testid={`spark-subs-${ch.channel_id}`}
          />
        </div>
        <div className="border border-zinc-900 rounded-sm p-2 bg-black/40">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-300 mb-1">
            views · last 30 days
          </div>
          <MiniSparkline
            series={series}
            dataKey="views"
            color="#22d3ee"
            testid={`spark-views-${ch.channel_id}`}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs mb-4">
        <Row k="Avg views / video" v={fmtNum(r.avg_views_per_video)} />
        <Row k="Posts / month" v={r.posts_per_month ?? "—"} />
        <Row
          k="Last upload"
          v={
            r.days_since_last_upload === null ||
            r.days_since_last_upload === undefined
              ? "—"
              : `${r.days_since_last_upload}d ago`
          }
        />
        <Row k="Created" v={fmtDate(ch.channel_created)} />
        <Row k="Est. monthly rev" v={fmtCurrency(r.est_monthly_revenue)} />
        <Row k="RPM" v={`$${r.rpm_used ?? "—"}`} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button
          data-testid={`active-channel-analyze-${ch.channel_id}`}
          onClick={() => onAnalyze(ch)}
          className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-300 border border-yellow-400/40 hover:border-yellow-400 hover:bg-yellow-400/10 px-3 py-1.5 rounded-sm transition-colors"
        >
          ▸ analyze
        </button>
        <button
          data-testid={`active-channel-videos-${ch.channel_id}`}
          onClick={() => onViewVideos(ch)}
          className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300 border border-cyan-400/40 hover:border-cyan-400 hover:bg-cyan-400/10 px-3 py-1.5 rounded-sm transition-colors"
          title="See view counts for every uploaded video"
        >
          ▤ videos
        </button>
        <button
          data-testid={`active-channel-next-video-${ch.channel_id}`}
          onClick={() => onNextVideo(ch)}
          className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300 border border-emerald-400/40 hover:border-emerald-400 hover:bg-emerald-400/10 px-3 py-1.5 rounded-sm transition-colors"
        >
          ▶ next video
        </button>
      </div>

      <div className="flex items-center justify-between mt-auto pt-3 border-t border-zinc-900">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
          synced {fmtDate(ch.last_snapshot_at)}
        </span>
        <div className="flex items-center gap-2">
          <button
            data-testid={`active-channel-refresh-${ch.channel_id}`}
            onClick={() => onRefresh(ch)}
            disabled={refreshing}
            className={
              refreshing
                ? "text-[10px] font-mono uppercase tracking-[0.2em] text-yellow-400 animate-pulse cursor-wait"
                : "text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-300 hover:text-yellow-400 transition-colors"
            }
            title={refreshing ? "Pulling fresh stats…" : "Pull fresh stats right now"}
          >
            {refreshing ? "↻ refreshing…" : "↻ refresh"}
          </button>
          <button
            data-testid={`active-channel-delete-${ch.channel_id}`}
            onClick={() => onDelete(ch)}
            className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-300 hover:text-red-400 transition-colors"
          >
            × remove
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-black border border-zinc-900 px-2.5 py-2 rounded-sm">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 mb-0.5">
        {label}
      </div>
      <div className="font-display text-base font-bold text-white leading-none">
        {value}
      </div>
      {sub && (
        <div className="font-mono text-[10px] text-zinc-400 mt-1">{sub}</div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-2 min-w-0">
      <span className="text-zinc-300 font-mono text-[10px] uppercase tracking-wider truncate">
        {k}
      </span>
      <span className="text-zinc-100 truncate">{v}</span>
    </div>
  );
}

// Corner overlay dot on the channel thumbnail signalling whether the
// user's YouTube OAuth session is linked to THIS specific channel.
//   • linked === true  → red YouTube-red dot with white ▶ (this
//     channel can publish directly)
//   • linked === false → muted grey dot with a slashed ▶ so it's
//     clearly a "not linked" state without needing a legend.
function YouTubeStatusDot({ linked }) {
  const colorCls = linked
    ? "bg-red-500 border-red-400 text-white"
    : "bg-zinc-800 border-zinc-700 text-zinc-500";
  const title = linked
    ? "Connected to YouTube — YT Maker can publish videos directly to this channel."
    : "Not connected to YouTube. Open the channel to link it.";
  return (
    <span
      data-testid={linked ? "yt-status-dot-linked" : "yt-status-dot-unlinked"}
      title={title}
      aria-label={title}
      className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full border-2 border-black grid place-items-center font-mono text-[9px] font-black leading-none ${colorCls}`}
    >
      <span
        aria-hidden
        className={linked ? "" : "opacity-70 line-through decoration-[1px]"}
      >
        ▶
      </span>
    </span>
  );
}

export default function ActiveChannels() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [input, setInput] = useState("");
  const [analyzeFor, setAnalyzeFor] = useState(null);
  const [ideaBankFor, setIdeaBankFor] = useState(null);
  const [videosFor, setVideosFor] = useState(null);
  // channel_ids currently being snapshot-refreshed against YouTube.
  // Drives the per-card "↻ refreshing…" pulse so the user knows the
  // page is actively pulling new stats.
  const [refreshingIds, setRefreshingIds] = useState(() => new Set());
  // The YouTube channel_id the current user has linked via OAuth
  // (empty string if nothing linked). Every ChannelCard uses this
  // to show its "linked / not linked" dot without a per-card fetch.
  const [linkedYtChannelId, setLinkedYtChannelId] = useState("");
  // Guard against React 18 StrictMode double-mount firing two
  // parallel bulk refreshes on first paint — we only auto-refresh
  // once per component mount.
  const didAutoRefreshRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listActiveChannels();
      setRows(data);
      // Re-populate the sidebar cache from this fresher list so newly
      // added/removed channels appear in the nav without a page reload.
      invalidateActiveChannelsNav();
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load channels");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Kick off a background snapshot refresh for every channel the user
  // is tracking. We paint the cached rows immediately (fast load), then
  // fan out `refreshActiveChannel` calls in parallel so every card
  // updates on its own as YouTube responds. Failures are swallowed
  // silently — a stale card is better than an angry error toast on
  // page navigation.
  const refreshAllInBackground = useCallback(async (channelIds) => {
    if (!channelIds || channelIds.length === 0) return;
    setRefreshingIds(new Set(channelIds));
    await Promise.allSettled(
      channelIds.map(async (cid) => {
        try {
          await refreshActiveChannel(cid);
        } catch {
          // Silent — the manual "↻ refresh" button surfaces detailed errors.
        } finally {
          setRefreshingIds((prev) => {
            const next = new Set(prev);
            next.delete(cid);
            return next;
          });
        }
      }),
    );
    // Once all snapshots finish, pull the freshly-updated list so
    // rollups + sparklines reflect the new numbers.
    try {
      const fresh = await listActiveChannels();
      setRows(fresh);
    } catch {
      // Non-fatal.
    }
  }, []);

  useEffect(() => {
    (async () => {
      const data = await load();
      if (didAutoRefreshRef.current) return;
      didAutoRefreshRef.current = true;
      const ids = (data || []).map((c) => c.channel_id).filter(Boolean);
      refreshAllInBackground(ids);
      // Fetch the YouTube OAuth link status once per mount so every
      // channel card can decide whether to show a connected or
      // disconnected dot. Failure is silent — the dot just falls back
      // to "not connected" and the detail page still lets users link.
      try {
        const s = await getYouTubeLinkStatus();
        if (s?.linked && s?.channel_id) setLinkedYtChannelId(s.channel_id);
      } catch { /* silent */ }
    })();
    // Intentional: run once per mount. Adding load / refreshAllInBackground
    // as deps would re-fire the bulk YouTube refresh on every render.
  }, [load, refreshAllInBackground]);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setAdding(true);
    try {
      const ch = await addActiveChannel({ url_or_handle: input.trim() });
      toast.success(`Added ${ch.name || "channel"} — first snapshot stored`);
      setInput("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add channel");
    } finally {
      setAdding(false);
    }
  };

  const onRefresh = async (ch) => {
    if (refreshingIds.has(ch.channel_id)) return;
    setRefreshingIds((prev) => new Set(prev).add(ch.channel_id));
    try {
      await refreshActiveChannel(ch.channel_id);
      toast.success("Stats refreshed");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshingIds((prev) => {
        const next = new Set(prev);
        next.delete(ch.channel_id);
        return next;
      });
    }
  };

  // Roll up the totals shown in the hero metric strip. Recomputes any
  // time `rows` changes (so auto-refresh reruns update the numbers
  // without a page reload). Cheap — the tracker's channel count is
  // always small enough to iterate synchronously.
  const heroMetrics = useMemo(() => {
    let totalSubs = 0;
    let totalViews = 0;
    for (const ch of rows || []) {
      totalSubs += Number(ch?.subscribers || 0);
      totalViews += Number(ch?.total_views || 0);
    }
    return { totalSubs, totalViews };
  }, [rows]);

  const onDelete = async (ch) => {
    if (!window.confirm(`Stop tracking ${ch.name}?`)) return;
    try {
      await deleteActiveChannel(ch.channel_id);
      toast.success("Removed");
      setRows((prev) => prev.filter((r) => r.channel_id !== ch.channel_id));
      invalidateActiveChannelsNav();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove");
    }
  };

  const onNextVideo = (ch) => {
    // Opens the Idea Bank modal — user picks one of 20 fresh ideas;
    // we then materialize SEO + thumbnail for the chosen idea.
    setIdeaBankFor(ch);
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1300px] mx-auto" data-testid="active-channels-page">
      {analyzeFor && (
        <AnalyzeModal
          channel={analyzeFor}
          onClose={() => setAnalyzeFor(null)}
        />
      )}
      {ideaBankFor && (
        <IdeaBankModal
          channel={ideaBankFor}
          onClose={() => setIdeaBankFor(null)}
        />
      )}
      {videosFor && (
        <ChannelVideosModal
          channel={videosFor}
          onClose={() => setVideosFor(null)}
        />
      )}
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
        // PHASE 4 · CONTENT FACTORY / MONITORING FLOOR
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-white mb-3 leading-none">
        Your launched channels.
      </h1>
      <p className="text-zinc-300 text-sm max-w-2xl mb-6 leading-relaxed">
        Track every YouTube channel you&apos;ve actually launched in the wild —
        subs, views, posting cadence, and niche-aware revenue. We snapshot
        stats once a day so growth charts fill in automatically.
      </p>

      {/* Hero metrics — reads like the primary monitoring console for the
          whole factory. Numbers derived client-side from the current row set
          so it stays in sync with the auto-refresh flow. */}
      <div
        data-testid="active-channels-hero-metrics"
        className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8"
      >
        <HeroMetric
          testid="metric-channels-tracked"
          label="Channels tracked"
          value={rows.length}
          unit={rows.length === 1 ? "channel" : "channels"}
        />
        <HeroMetric
          testid="metric-total-subscribers"
          label="Total subscribers"
          value={formatCompactNumber(heroMetrics.totalSubs)}
        />
        <HeroMetric
          testid="metric-total-views"
          label="Lifetime views"
          value={formatCompactNumber(heroMetrics.totalViews)}
        />
        <HeroMetric
          testid="metric-refresh-status"
          label={refreshingIds.size > 0 ? "Refreshing" : "Snapshot"}
          value={refreshingIds.size > 0 ? refreshingIds.size : "✓"}
          unit={refreshingIds.size > 0
            ? (refreshingIds.size === 1 ? "channel" : "channels")
            : "live"}
        />
      </div>

      {refreshingIds.size > 0 && (
        <div
          data-testid="active-channels-auto-refresh-banner"
          className="mb-6 border border-yellow-400/40 bg-yellow-400/5 rounded-sm px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-yellow-300 animate-pulse"
        >
          ↻ pulling fresh stats for {refreshingIds.size} channel{refreshingIds.size === 1 ? "" : "s"}…
        </div>
      )}

      <FactorySection
        station="01"
        title="INTAKE"
        description="Paste a YouTube channel URL, @handle, or channel ID. Once tracked, we snapshot it every 24 hours."
        data-testid="active-channels-intake-section"
      >
        <form
          onSubmit={onAdd}
          className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-5 flex flex-col sm:flex-row gap-3"
          data-testid="active-channels-add-form"
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Paste a YouTube channel URL, @handle, or channel ID"
            data-testid="active-channels-add-input"
            className="flex-1 bg-black border border-zinc-800 hover:border-yellow-400/60 focus:border-yellow-400 text-sm text-zinc-100 font-mono px-4 py-2.5 rounded-sm focus:outline-none transition-colors"
          />
          <button
            type="submit"
            disabled={adding || !input.trim()}
            data-testid="active-channels-add-btn"
            className="font-display text-sm font-black uppercase tracking-wide bg-yellow-400 text-black hover:bg-yellow-300 disabled:bg-zinc-700 disabled:text-zinc-500 px-5 py-2.5 rounded-sm transition-colors shrink-0"
          >
            {adding ? "▸ adding…" : "▸ Track channel"}
          </button>
        </form>
      </FactorySection>

      <ConveyorDivider label="monitoring floor" />

      <FactorySection
        station="02"
        title="MONITORING GRID"
        description="Every card is a live monitoring station. Stats refresh automatically on visit; use ↻ refresh for an on-demand pull."
        actions={
          rows.length > 0 && (
            <StatusChip
              variant={refreshingIds.size > 0 ? "amber" : "emerald"}
              data-testid="grid-status-chip"
            >
              {refreshingIds.size > 0
                ? `↻ ${refreshingIds.size} refreshing`
                : `✓ ${rows.length} live`}
            </StatusChip>
          )
        }
        data-testid="active-channels-monitoring-section"
      >
        {loading ? (
          <div className="font-mono text-sm text-zinc-500" data-testid="active-channels-loading">
            [recon] loading<span className="blink">_</span>
          </div>
        ) : rows.length === 0 ? (
          <div
            className="border border-dashed border-zinc-800 rounded-sm p-10 text-center"
            data-testid="active-channels-empty"
          >
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
              // NO ACTIVE CHANNELS YET
            </div>
            <p className="text-zinc-300 text-sm max-w-md mx-auto">
              Launched a channel? Paste its URL or @handle above and we&apos;ll start
              tracking growth from day one.
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-5"
            data-testid="active-channels-grid"
          >
            {rows.map((ch) => (
              <ChannelCard
                key={ch.channel_id}
                ch={ch}
                onRefresh={onRefresh}
                onDelete={onDelete}
                onAnalyze={setAnalyzeFor}
                onNextVideo={onNextVideo}
                onViewVideos={setVideosFor}
                refreshing={refreshingIds.has(ch.channel_id)}
                ytLinked={
                  !!linkedYtChannelId
                  && linkedYtChannelId === ch.channel_id
                }
              />
            ))}
          </div>
        )}
      </FactorySection>
    </div>
  );
}

/** Compact number formatter for the hero metrics — 12,300 → "12.3K",
 *  1,234,567 → "1.2M". Keeps the amber numerals readable at a glance
 *  on the monitoring console without overflowing the tile. */
function formatCompactNumber(n) {
  const num = Number(n) || 0;
  if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return String(num);
}
